import axios from 'axios'

const client = axios.create({ baseURL: process.env.VUE_APP_API_URL || '/api', timeout: 30000 })
const ROOT = '/real-positions/stage5'
export const HISTORY_LIMIT = 100

function fail(message) {
  throw new Error(message)
}

function object(value) {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function text(value) {
  return typeof value === 'string' && value.trim().length > 0
}

function readonlyContract(payload, type) {
  const contract = payload && payload.page_contract
  if (!object(payload) || payload.ok !== true || payload.payload_type !== type ||
      payload.read_only !== true || payload.writes_database !== false || !object(contract) ||
      contract.scope !== 'broker_screenshot_real_position_snapshot' ||
      contract.stored_timestamp_timezone !== 'UTC' || contract.snapshot_detail_read_enabled !== true ||
      contract.direct_snapshot_write_enabled !== false || contract.creates_real_order !== false) {
    fail('持仓读取合同不匹配，请确认前后端版本已配套部署。')
  }
}

function header(row) {
  if (!object(row) || !text(row.snapshot_id) || !text(row.snapshot_as_of) ||
      row.source_type !== 'broker_snapshot' || !text(row.verification_status) ||
      !Number.isInteger(row.positions_count) || row.positions_count < 0) {
    fail('快照摘要不完整，无法确认持仓状态。')
  }
}

export function validateHistory(payload) {
  readonlyContract(payload, 'broker_position_snapshot_history')
  if (payload.append_only !== true || !Array.isArray(payload.items) ||
      payload.count !== payload.items.length || payload.count > HISTORY_LIMIT) {
    fail('快照历史不完整，不能视为空仓。')
  }
  const ids = new Set()
  payload.items.forEach(row => {
    header(row)
    if (ids.has(row.snapshot_id)) fail('快照历史包含重复编号。')
    ids.add(row.snapshot_id)
  })
  return payload
}

export function validateSnapshot(payload, expected) {
  readonlyContract(payload, 'broker_position_snapshot_readback')
  const checks = payload.checks
  if (payload.all_checks_match !== true || !object(checks) ||
      !['snapshot_present', 'positions_count', 'row_digests', 'append_only_identity'].every(key => checks[key] === true) ||
      Object.keys(checks).some(key => checks[key] !== true)) {
    fail('快照完整性校验失败，持仓明细暂不展示。')
  }
  header(payload.snapshot)
  const snapshot = payload.snapshot
  if (snapshot.verification_status !== 'confirmed' || payload.snapshot_id !== expected.snapshot_id ||
      snapshot.snapshot_id !== expected.snapshot_id || !Array.isArray(payload.positions) ||
      snapshot.positions_count !== payload.positions.length) {
    fail('快照编号、确认状态或明细数量不一致。')
  }
  for (const key of ['snapshot_as_of', 'positions_count', 'source_payload_digest', 'evidence_id', 'account_alias']) {
    if (snapshot[key] !== expected[key]) fail('快照历史与明细不一致，请刷新后重试。')
  }
  const rowIds = new Set()
  payload.positions.forEach(row => {
    if (!object(row) || !text(row.row_id) || rowIds.has(row.row_id) || !text(row.ts_code) ||
        numericValue(row.quantity) === null || numericValue(row.quantity) < 0) {
      fail('持仓明细缺少有效编号、代码或数量。')
    }
    rowIds.add(row.row_id)
  })
  return payload
}

export function numericValue(value) {
  if (typeof value !== 'number' && (typeof value !== 'string' || !/^-?\d+(?:\.\d+)?$/.test(value))) return null
  const number = Number(value)
  return Number.isFinite(number) ? number : null
}

export function formatNumber(value, digits = 2) {
  const number = numericValue(value)
  return number === null ? '—' : number.toLocaleString('zh-CN', { minimumFractionDigits: digits, maximumFractionDigits: digits })
}

// The canonical writer stores UTC-naive database timestamps. Never use the browser's local zone.
export function snapshotTime(value) {
  if (typeof value !== 'string') return null
  const match = value.trim().match(/^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2}:\d{2})(?:\.(\d{1,6}))?(Z|[+-]\d{2}:\d{2})?$/)
  if (!match) return null
  const raw = match[1] + 'T' + match[2] + (match[3] ? '.' + match[3].slice(0, 3) : '')
  const utc = Date.parse(raw + 'Z')
  if (!Number.isFinite(utc) || new Date(utc).toISOString().slice(0, 19) !== raw.slice(0, 19)) return null
  const time = Date.parse(raw + (match[4] || 'Z'))
  return Number.isFinite(time) ? time : null
}

export function formatTime(value) {
  const time = snapshotTime(value)
  if (time === null) return '时间待核验'
  return new Date(time + 8 * 3600000).toISOString().slice(0, 19).replace('T', ' ') + ' 北京时间'
}

export function snapshotFreshness(value, now) {
  const time = snapshotTime(value)
  if (time === null || !Number.isFinite(now)) return { type: 'warning', label: '时间待核验', age: '无法计算距今时长' }
  const minutes = (now - time) / 60000
  if (minutes < 0) return { type: 'danger', label: '时间异常', age: '快照时间晚于本次读取时间' }
  const age = minutes < 1 ? '不足 1 分钟' : minutes < 60 ? Math.floor(minutes) + ' 分钟' :
    minutes < 1440 ? Math.floor(minutes / 60) + ' 小时 ' + Math.floor(minutes % 60) + ' 分钟' :
      Math.floor(minutes / 1440) + ' 天 ' + Math.floor(minutes % 1440 / 60) + ' 小时'
  return { type: minutes <= 30 ? 'info' : 'warning', label: minutes <= 30 ? '距今 30 分钟内' : minutes <= 120 ? '距今 2 小时内' : '距今超过 2 小时', age }
}

export function realPositionsError(error) {
  const status = error && error.response && error.response.status
  if (status === 404) return '未找到快照或读取接口尚未部署，请刷新或检查部署版本。'
  if (status === 409) return '快照完整性校验失败，持仓明细暂不展示。'
  if (status >= 500) return '持仓读取服务暂不可用，请稍后重试。'
  return error && error.message ? error.message : '持仓读取失败，请重试。'
}

function read(path, params = {}) {
  return client.get(ROOT + path, { params: { ...params, _: Date.now() }, headers: { 'Cache-Control': 'no-cache' } }).then(response => response.data)
}

export const realPositionsApi = {
  history: () => read('/history', { limit: HISTORY_LIMIT }).then(validateHistory),
  snapshot: row => read('/snapshots/' + encodeURIComponent(row.snapshot_id)).then(payload => validateSnapshot(payload, row))
}
